import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  FieldGrid,
  Modal,
  type SelectOption,
} from '@skupreme/shared/ui';
import { FormNumberField, FormSelect } from '@skupreme/shared/ui/form';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const schema = z.object({
  regionId: z.string().min(1, 'Region is required'),
  price: z.number({
    required_error: 'Price is required',
    invalid_type_error: 'Price is required',
  }),
});

type InternationalValues = { regionId: string; price: number | null };

type InternationalShippingModalProps = {
  isOpen: boolean;
  regionOptions: SelectOption[];
  currency: string;
  onClose: () => void;
  onConfirm: (regionId: string, price: number) => void;
};

/** A region abroad the provider ships to, and the fee for it. */
export function InternationalShippingModal({
  isOpen,
  regionOptions,
  currency,
  onClose,
  onConfirm,
}: InternationalShippingModalProps) {
  const { control, handleSubmit, reset } = useForm<InternationalValues>({
    resolver: zodResolver(schema),
    defaultValues: { regionId: '', price: null },
  });

  useEffect(() => {
    if (isOpen) reset({ regionId: '', price: null });
  }, [isOpen, reset]);

  const save = handleSubmit(values =>
    onConfirm(values.regionId, values.price ?? 0),
  );

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={open => !open && onClose()}
      title="Add international shipping"
      showCloseButton
      size="md"
      footer={
        <>
          <Button variant="ghost" onPress={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onPress={() => save()}>
            Add
          </Button>
        </>
      }
    >
      <form noValidate autoComplete="off" onSubmit={save}>
        <FieldGrid columns={2}>
          <FormSelect
            control={control}
            name="regionId"
            label="Region"
            options={regionOptions}
            placeholder="Select region"
            isRequired
          />
          <FormNumberField
            control={control}
            name="price"
            label={`Price (${currency})`}
            placeholder="1.00"
            isRequired
          />
        </FieldGrid>
      </form>
    </Modal>
  );
}
